import { useState } from 'react'

import googleIcon from 'src/assets/icons/google.svg'
import microsoftIcon from 'src/assets/icons/microsoft.svg'
import { cn } from 'src/helpers/style'
import i18n from 'src/i18n'

import {
  Typography,
  TypographyVariant,
  TypographyWeight,
} from 'src/components/atoms'
import {
  Dialog,
  DialogDescription,
  DialogTitle,
} from 'src/components/molecules/dialog'

import { OAuthProvider } from 'src/app/auth/interfaces'

export interface ILoginProviderModalProps {
  onProviderSignIn: (provider: OAuthProvider) => void
  loadingProvider?: OAuthProvider
}

interface IOAuthOption {
  provider: OAuthProvider
  label: string
  iconSrc: string
}

const OAUTH_OPTIONS: readonly IOAuthOption[] = [
  {
    provider: 'google',
    label: i18n.t('auth.login.continueWithGoogle'),
    iconSrc: googleIcon,
  },
  {
    provider: 'microsoft',
    label: i18n.t('auth.login.continueWithMicrosoft'),
    iconSrc: microsoftIcon,
  },
] as const

const triggerClassName = cn(
  'group inline-flex w-fit self-start items-center justify-center gap-2 rounded-full px-5 py-2',
  'border border-surface-raised bg-transparent text-surface-raised',
  'transition-colors duration-200',
  'hover:bg-surface-raised hover:text-content-primary',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-surface-raised focus-visible:ring-offset-2 focus-visible:ring-offset-transparent',
  'disabled:cursor-not-allowed disabled:opacity-60',
)

const ssoButtonClassName = cn(
  'flex h-11 w-full items-center justify-center gap-2 rounded-full px-6',
  'border-[0.5px] border-border-strong bg-surface-raised text-content-primary',
  'transition-colors duration-200',
  'hover:bg-state-hover-fill',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-accent focus-visible:ring-offset-2 focus-visible:ring-offset-surface-page',
  'disabled:cursor-not-allowed disabled:opacity-60',
)

const dialogContentClassName = cn(
  'w-[417px] max-w-[calc(100vw-64px)]',
  'rounded-[20px] bg-surface-page p-8 text-content-primary shadow-xl',
  'flex flex-col items-center gap-6',
)

export const LoginProviderModal = ({
  onProviderSignIn,
  loadingProvider,
}: ILoginProviderModalProps): JSX.Element => {
  const [isOpen, setIsOpen] = useState(false)
  const isAnyLoading = loadingProvider !== undefined

  const trigger = (
    <button
      type="button"
      onClick={() => setIsOpen(true)}
      className={triggerClassName}
    >
      <Typography
        variant={TypographyVariant.bodyXsStrong}
        className="text-surface-raised transition-colors duration-200 group-hover:text-content-primary"
      >
        {i18n.t('auth.login.login')}
      </Typography>
    </button>
  )

  return (
    <>
      {trigger}
      <Dialog
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        overlayClassName="bg-black/20"
        contentClassName={dialogContentClassName}
      >
        <div className="flex w-full flex-col gap-4">
          <div className="flex flex-col gap-3">
            <DialogTitle asChild>
              <Typography
                variant={TypographyVariant.titleMd}
                weight={TypographyWeight.light}
                className="whitespace-pre-line font-display text-content-primary"
              >
                {'Welcome,\nLimited Partners.'}
              </Typography>
            </DialogTitle>

            <DialogDescription asChild>
              <Typography
                variant={TypographyVariant.bodyXxs}
                className="leading-normal text-content-secondary"
              >
                {i18n.t('auth.login.signInToContinue')}
              </Typography>
            </DialogDescription>
          </div>
          <div className="h-px w-full bg-border" aria-hidden />
        </div>

        <div className="flex w-full flex-col gap-2">
          {OAUTH_OPTIONS.map(({ provider, label, iconSrc }) => {
            const isLoading = loadingProvider === provider
            return (
              <button
                key={provider}
                type="button"
                onClick={() => onProviderSignIn(provider)}
                disabled={isAnyLoading}
                aria-busy={isLoading}
                className={ssoButtonClassName}
              >
                <img
                  src={iconSrc}
                  alt=""
                  aria-hidden
                  className="size-4 shrink-0"
                />
                <Typography
                  variant={TypographyVariant.bodySmStrong}
                  className="text-content-primary"
                >
                  {isLoading ? i18n.t('auth.login.signingIn') : label}
                </Typography>
              </button>
            )
          })}
        </div>

        <Typography
          variant={TypographyVariant.bodyXxs}
          className="text-center text-content-secondary"
        >
          {i18n.t('auth.login.securePortal')}
        </Typography>
      </Dialog>
    </>
  )
}
